import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { PlIcon } from '@app/icon-library/icons';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ModalAddToCalendarComponent,
  ModalAddToCalendarInputs,
} from '@app/components/lifeAct/modalAddToCalendar/modalAddToCalendar.component';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';
import { NgOnInitWriteBugAbstractComponent } from '@shared/abstract/ngOnInitWriteBug.abstract.component';

export interface LifeActValue {
  note: string | null;
  completed: boolean;
}

@Component({
  selector: 'pl-lifeact',
  templateUrl: './lifeAct.component.html',
  styleUrls: ['./lifeAct.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LifeActComponent
  extends NgOnInitWriteBugAbstractComponent<LifeActValue>
  implements OnInit, ControlValueAccessor
{
  @Input({ required: true }) public title!: string;
  @Input({ required: true }) public subTitle!: string;
  @Input({ required: true }) public description!: string;
  @Input({ required: true }) public calendarTitle!: string;
  @Input({ required: true }) public calendarDescription!: string;
  @Input({ required: true }) public timeFormat!: TimeFormat;
  @Input() public isDisabled: boolean = false;

  public note: string = '';
  public markAsComplete: WritableSignal<boolean> = signal(false);

  @Output() public noteChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() public completed: EventEmitter<boolean> = new EventEmitter<boolean>();

  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;

  public isPanelOpen: boolean = false;

  constructor(
    @Optional() @Self() public override controlDir: NgControl,
    private dialog: MatDialog,
  ) {
    super(controlDir);
  }

  public plWriteValue(valueToWrite: LifeActValue | undefined): void {
    if (valueToWrite !== undefined) {
      this.isPanelOpen = !!valueToWrite.note;

      this.note = valueToWrite.note || '';
      this.markAsComplete.set(valueToWrite.completed);
    }
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  public plOnInit(): void {
    this.isPanelOpen = !!this.note;
  }

  public onAddToCalendarClicked(): void {
    this.dialog.open<ModalAddToCalendarComponent, ModalAddToCalendarInputs>(ModalAddToCalendarComponent, {
      autoFocus: false,
      data: {
        title: this.calendarTitle,
        description: this.calendarDescription,
        timeFormat: this.timeFormat,
      },
      maxHeight: '100vh',
      maxWidth: '700px',
      width: '100%',
    });
  }

  public toggleMarkAsComplete(): void {
    this.markAsComplete.set(!this.markAsComplete());
    if (this.markAsComplete()) {
      this.isPanelOpen = true;
    }
    this.emitChanges();
  }

  public onNoteChange(noteUpdated: string): void {
    if (this.note !== noteUpdated) {
      this.note = noteUpdated;
      this.emitChanges();
    }
  }

  private emitChanges(): void {
    this.onChangeCallback({
      note: this.note,
      completed: this.markAsComplete(),
    });
  }
}
